import { Atom, LineChart, ShieldCheck } from 'lucide-react';

const pillars = [
  { icon: Atom, title: 'Molecular Precision', desc: 'Sequence-verified peptides with molecular identity confirmed by LC-MS on every lot.' },
  { icon: LineChart, title: 'Analytical Rigor', desc: 'HPLC purity profiling that consistently exceeds 99% chromatographic purity.' },
  { icon: ShieldCheck, title: 'Traceable Supply', desc: 'Complete chain-of-custody documentation from synthesis through to shipment.' },
];

export default function ScienceSection() {
  return (
    <section id="science" data-testid="science-section" className="py-24 lg:py-32 bg-[#F6FBFB]">
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          {/* Text */}
          <div className="lg:col-span-6 order-2 lg:order-1">
            <span className="text-xs tracking-[0.25em] uppercase text-[#0A7C84] mb-4 block" style={{ fontFamily: 'var(--font-mono)' }}>
              The Science
            </span>
            <h2
              className="text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#0A1128] mb-6 leading-[1.05]"
              style={{ fontFamily: 'var(--font-heading)', fontWeight: 900 }}
              data-testid="science-heading"
            >
              Purity you can put
              <br />
              under a microscope.
            </h2>
            <p className="text-base md:text-lg text-[#495057] leading-relaxed mb-10 max-w-xl" style={{ fontFamily: 'var(--font-body)' }}>
              Every Biolyn compound is engineered for reproducibility. From synthesis to final release, our peptides pass a multi-stage analytical gauntlet — so the data in your lab matches the data on our Certificate of Analysis.
            </p>

            <div className="space-y-6">
              {pillars.map((p) => (
                <div key={p.title} className="flex gap-4" data-testid={`science-pillar-${p.title.toLowerCase().replace(/ /g, '-')}`}>
                  <div className="shrink-0 w-11 h-11 rounded-full bg-white border border-[#D4EAEC] flex items-center justify-center">
                    <p.icon className="w-5 h-5 text-[#0A7C84]" strokeWidth={1.75} />
                  </div>
                  <div>
                    <h3 className="text-base text-[#0A1128] mb-1" style={{ fontFamily: 'var(--font-heading)', fontWeight: 700 }}>{p.title}</h3>
                    <p className="text-sm text-[#6C757D] leading-relaxed" style={{ fontFamily: 'var(--font-body)' }}>{p.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Image */}
          <div className="lg:col-span-6 order-1 lg:order-2">
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1771015138302-b87d00115bda?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHw0fHxtb2xlY3VsYXIlMjBzdHJ1Y3R1cmUlMjBzY2llbmNlJTIwcmVzZWFyY2glMjBibHVlfGVufDB8fHx8MTc5MDYzNzQxOXww&ixlib=rb-4.1.0&q=85"
                alt="Molecular research visualization"
                className="w-full aspect-[4/5] lg:aspect-[4/4.4] object-cover"
                data-testid="science-image"
              />
              <div className="absolute -bottom-6 -left-4 sm:-left-6 bg-white border border-[#E9ECEF] px-6 py-5 shadow-[0_24px_48px_-28px_rgba(10,17,40,0.4)]">
                <span className="block text-3xl text-[#0A1128]" style={{ fontFamily: 'var(--font-heading)', fontWeight: 900 }}>99.4%</span>
                <span className="text-[10px] tracking-[0.2em] uppercase text-[#0A7C84]" style={{ fontFamily: 'var(--font-mono)' }}>Avg. Batch Purity</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
